<template>
	<div id="domain-edit" class="container panel">
		<div class="row">
			<div class="col-md-3 sidebar">
				<side-bar></side-bar>
			</div>

			<div class="col-md-9 content">
				<dash-head :title="name" subtitle="Password Security Portal"></dash-head>		

				<hr class="mt-3 mb-4">

				<error :error="error"></error>

				<div class="row mt-3">
					<div class="col-md-12">
						<chart type="line" @chart-complete="chartComplete"
							:width="200" 
							:height="50" 
							:responsive="true" 
							:useBlank="false" 
							:values="chartValues" 	
							:backgroundColors="['rgba(255, 159, 64, 0.2)']"
							:borderColors="['rgba(255, 159, 64, 1)']">
						</chart>
					</div>
				</div>

				<div class="clearfix mt-1 mb-3" v-if="!showEditDomain">
					<div class="float-left ml-3">
					</div>
					<div class="float-right mr-3">
						<a class="btn btn-outline-primary px-3" :download="name + ' chart.png'" :disabled="chartBase64==null" :href="chartBase64">Export</a>								
					</div>
				</div>

				<div v-if="domain.companyData">
					<hr class="mt-3 mb-3">

					<div class="row mt-3">
						<div class="col-md-12">
							<p class="font-weight-normal">{{companyData.description || ''}}</p>
						</div>
					</div>

					<div class="row mt-3">
						<div class="col-md-6">
							<dl>
								<dt>Name</dt>
								<dd>{{companyData.name || ''}}</dd>

								<dt>LinkedIn</dt>
								<dd><a :href="linkedInUrl" target="_blank">{{linkedInName}}</a></dd>

								<dt>Twitter</dt>
								<dd><a :href="twitterUrl" target="_blank">{{twitterName}}</a></dd>

								<dt>Tech</dt>
								<dd><expanding-list :items="techValues"></expanding-list></dd>

								<dt>Domain Aliases</dt>
								<dd><expanding-list :items="domainValues"></expanding-list></dd>

							</dl>
						</div>
						<div class="col-md-6">
							<dl>
								<dt>Time Zone</dt>
								<dd>{{companyData.timeZone || ''}}</dd>

								<dt>Location</dt>
								<dd>{{companyData.location || ''}}</dd>

								<dt>Emails</dt>
								<dd><expanding-list :items="emailValues"></expanding-list></dd>
							</dl>	
						</div>
					</div>
				</div>

				<hr class="mt-1 mb-3">

				<div class="row mt-3" v-if="!showEditDomain">
  					<div class="col-md-6">
						<dl>
							<dt>Domain</dt>
							<dd>{{domain.value}}</dd>

							<dt>Status</dt>
							<dd>{{domain.statusDescription}}</dd>

							<dt>Industry</dt>
							<dd>{{domain.industryDescription}}</dd>
						</dl>
					</div>
					<div class="col-md-6">
						<dl>
							<dt>Employees</dt>
							<dd>{{domain.total}}</dd>

							<dt>Domain Credentials</dt>
							<dd>{{domain.totalBreached}}</dd>

							<dt>Total Score</dt>
							<dd>{{domain.totalScore}}</dd>
						</dl>
						<div class="text-right mt-4">
							
						</div>
					</div>
				</div>

				<div class="row mt-3" v-if="showEditDomain">
					<div class="col-md-12">
						<edit-domain-form :domain="name" :industry="domain.industry" :userTotal="domain.total" @close-edit-domain="showHideEditDomain(false)"></edit-domain-form>
					</div>
				</div>

				<div class="clearfix mt-1 mb-3" v-if="!showEditDomain">
					<div class="float-left ml-3">
						<button type="button" class="btn btn-outline-primary px-3" @click="openModal">Remove</button>
						<button type="button" class="btn btn-outline-primary px-3" @click="reprocessDomain">Reprocess</button>
					</div>
					<div class="float-right mr-3">
						<button class="btn btn-outline-primary px-3" @click.prevent="showHideEditDomain(true)">Edit</button>								
					</div>
				</div>

			</div>
		</div>	

		<modal id="remove-domain-modal" :visible="showModal" modalId="remove-domain-modal" confirm="Remove" title="Confirm Remove Domain" @confirm="confirmModal" @cancel="cancelModal">
			<p class="mt-3">Are you sure you wish to remove this domain from your account?</p>
		</modal>
	</div>
</template>

<script>
	import SideBar from './SideBar'
	import DashHead from './DashHead'
	import EditDomainForm from './EditDomainForm'
	import Error from './Error'
	import Modal from './Modal'
	import Chart from './Chart'
	import ExpandingList from './ExpandingList'

	import api from '../api'
	import router from '../router'
	import store from '../state'

	export default {
		name: 'DomainEditPanel',
		components: {SideBar, DashHead, EditDomainForm, Error, Modal, Chart, ExpandingList},
		data() {
			return {
				showEditDomain: false,
				error: null,
				showModal: false,
				chartBase64: null
			}
		},
		methods: {
			showHideEditDomain(value) {
				this.showEditDomain = value;
			},
			openModal() {
				this.showModal = true;
			},
			cancelModal() {
				this.showModal = false;
			},
			confirmModal() {
				this.showModal = false;
				this.removeDomain();				
			},
			async removeDomain() {
				try
				{
					var response = await api.delete(`/domain/${this.domain.value}`);	
					store.dispatch('getDomainsAsync');

					//Navigate to the domains page
					router.push({name: 'domains'});
				}
				catch(err) {				
					this.error = err + ""; //Get the string representation of the error		
				}
			},
			async reprocessDomain() {
				try
				{
					var response = await api.put(`/domain/reprocess/${this.domain.value}`);	
					store.dispatch('getDomainsAsync');

					//Navigate to the domains page
					router.push({name: 'domains'});
				}
				catch(err) {				
					this.error = err + ""; //Get the string representation of the error		
				}
			},
			chartComplete(value) {
				this.chartBase64 = value;
			}
		},
		computed: {
			audit() {
				//Loop through all the audits, looking for this domain
				let lastAudit = null;
				
				for (var audit of store.state.audits) {
					if (audit.domain === this.name) {
						if (!lastAudit || audit.date > lastAudit.date) lastAudit = audit;
					}
				}
				
				return lastAudit;
			},
			name() {
				return router.currentRoute.params.name;
			},
			domain() {
				return store.getters.getDomain(router.currentRoute.params.name);
			},
			companyData() {
				return this.domain.companyData || {};
			},
			domainProcessed() {
				return this.domain.statusDescription == 'Completed';
			},
			linkedInUrl() {
				if (!this.companyData.linkedinHandle) return 'https://www.linkedin.com';
				return "https://www.linkedin.com/" + this.companyData.linkedinHandle;
			},
			linkedInName() {
				if (!this.companyData.linkedinHandle) return '(Not Found)';
				return this.companyData.linkedinHandle;
			},
			twitterUrl() {
				if (!this.companyData.twitterHandle) return 'https://twitter.com';
				return "https://twitter.com/" + this.companyData.twitterHandle;
			},
			twitterName() {
				if (!this.companyData.twitterHandle) return '(Not Found)';
				return this.companyData.twitterHandle;
			},
			techValues() {
				if (!this.companyData.tech) return [];
				
				//Remove dashes
				const values = this.companyData.tech.replace(/_/g, ' ');
				return values.split(',');
			},
			emailValues() {
				if (!this.companyData.siteEmailAddresses) return [];
				const values = this.companyData.siteEmailAddresses.toLowerCase();
				
				//Remove duplicates
				return [...new Set(values.split(','))];  
			},
			domainValues() {
				if (!this.companyData.domainAliases) return [];
				
				//Remove dashes
				const values = this.companyData.domainAliases;
				return values.split(',');
			},
			chartValues() {
				const result = [];
				
				//Loop through the domain 
				if (this.domain && this.domain.sourceAdditions) {
					for (var source of this.domain.sourceAdditions) {
						const splits = source.split(':'),
								name = splits[0],
								additions = parseInt(splits[1]);

						if (additions > 0) {
							const entity = store.getters.getSource(name);

							if (entity) {
								const desc = (entity.name === 'other') ? entity.rowKey : entity.name;

								result.push({
									t:entity.date, y:0, additions:additions, customLabel:desc, customLabelAfter: additions + ' credentials'
								});
							}
						}
					}
				};

				//Sort by date
				result.sort(function(a,b){
					// Turn your strings into dates, and then subtract them
					// to get a value that is either negative, positive, or zero.
					return new Date(a.t) - new Date(b.t);
				});

				//Finally, loop through the results and add each value together
				let total =0;
				for (var item of result) {
					total+=item.additions;
					item.y=total;
				}
		
				return result;
			}
		}
	}
</script>


// WEBPACK FOOTER //
// src/components/DomainEditPanel.vue